import { useState, useEffect, useCallback } from 'react';
import { sweepstakesApi, type ContestView, type LeaderboardView } from '../api/sweepstakes';
import { useAuthStore } from '../store/authStore';

/**
 * Hook to fetch the active/recent sweepstakes contest for a collection.
 */
export function useCollectionSweepstakes(collectionId: string | undefined) {
  const [contestView, setContestView] = useState<ContestView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContest = useCallback(async () => {
    if (!collectionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await sweepstakesApi.getForCollection(collectionId);
      setContestView(data.contest ? data as ContestView : null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [collectionId]);

  useEffect(() => {
    fetchContest();
  }, [fetchContest]);

  return { contestView, loading, error, refetch: fetchContest };
}

/**
 * Hook to fetch the leaderboard for a sweepstakes contest.
 * Automatically includes the connected wallet's rank if authenticated.
 */
export function useSweepstakesLeaderboard(contestId: string | undefined, frozen: boolean = false) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardView | null>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuthStore();

  const fetchLeaderboard = useCallback(async () => {
    if (!contestId) return;
    setLoading(true);
    try {
      const data = await sweepstakesApi.getLeaderboard(contestId, user?.xrpl_address);
      setLeaderboard(data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  }, [contestId, user?.xrpl_address]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Auto-refresh every 30 seconds while mounted (skip when frozen/ended)
  useEffect(() => {
    if (!contestId || frozen) return;
    const interval = setInterval(fetchLeaderboard, 30_000);
    return () => clearInterval(interval);
  }, [contestId, frozen, fetchLeaderboard]);

  return { leaderboard, loading, refetch: fetchLeaderboard };
}

/**
 * Hook to fetch the set of eligible offer IDs for a contest.
 * Used for client-side filtering on the collection browse page.
 */
export function useEligibleOffers(contestId: string | undefined) {
  const [eligibleOfferIds, setEligibleOfferIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contestId) return;
    setLoading(true);
    sweepstakesApi.getEligibleOffers(contestId)
      .then(data => setEligibleOfferIds(new Set(data.offerIds)))
      .catch(err => console.error('Failed to fetch eligible offers:', err))
      .finally(() => setLoading(false));
  }, [contestId]);

  return { eligibleOfferIds, loading };
}

/**
 * Hook to fetch the current user's entry stats for a contest.
 */
export function useMyEntries(contestId: string | undefined) {
  const [entryCount, setEntryCount] = useState(0);
  const [rank, setRank] = useState<number | null>(null);
  const { user } = useAuthStore();

  useEffect(() => {
    if (!contestId || !user) return;
    sweepstakesApi.getMyEntries(contestId)
      .then(data => {
        setEntryCount(data.entryCount);
        setRank(data.rank);
      })
      .catch(err => console.error('Failed to fetch my entries:', err));
  }, [contestId, user]);

  return { entryCount, rank };
}
